import { Link, router } from "@inertiajs/react"
import { CheckCheck, Send } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { CandidateReviewCard } from "@/components/candidates/candidate-review-card"
import { EmptyState } from "@/components/empty-state"
import { HomeTriageSection } from "@/components/home-triage/home-triage-section"
import { Button } from "@/components/ui/button"
import { candidatesPath } from "@/routes"
import type { CandidatesToReviewSection as SectionData } from "@/types/home-triage"

/**
 * OKS-951 — Brand/Home § « À relancer ».
 *
 * `contacted` candidates who have gone quiet and are due a follow-up. Per PO
 * this is designed as the MOST-USED segment in the product, not a secondary
 * surface — so it reuses the shipped {@link HomeTriageSection} shell and
 * {@link CandidateReviewCard} verbatim rather than forking either.
 *
 * **Why a separate section and not a widened sibling.** Membership here is
 * ATTEMPT-DERIVED — it depends on how many contact attempts fall inside the
 * candidate's current cadence cycle, against this brand's configured offsets —
 * which cannot be expressed as a status list at all. Passing
 * `statuses: ["contacted"]` to the shipped query would put every contacted
 * candidate in the section regardless of whether they are actually due, which
 * is the "wrong rows in a right-looking section" failure OKS-950 named.
 *
 * **Cold start vs empty are different states with different copy** (the same
 * argument OKS-950 made): a brand that has never contacted anyone gets pointed
 * at the `approved` tab to start, while a brand that is caught up is told so.
 * Conflating them would tell someone who just cleared their queue that they
 * haven't started.
 *
 * **The card cue and the queue deliberately disagree, and that is the design.**
 * The cue shows TOTAL attempts (« 3 tentatives · dernière il y a 5 j ») while
 * membership is decided per-cycle. A revert restarts the clock but not the
 * history — the history is the audit, the queue is the workflow (UX Spec §3).
 *
 * **Ordering is oldest-cycle-first**, on the shipped `WAITING_SINCE_ORDER_SQL`.
 * Stale/attention cueing is the server-side threshold — never recomputed on the
 * client.
 */
export interface CandidatesToFollowUpSectionProps {
  section: SectionData
}

export function CandidatesToFollowUpSection({
  section,
}: CandidatesToFollowUpSectionProps) {
  const { t } = useTranslation()
  const [isRetrying, setIsRetrying] = useState(false)

  function handleRetry() {
    setIsRetrying(true)
    router.reload({
      only: ["candidates_to_follow_up"],
      onFinish: () => setIsRetrying(false),
    })
  }

  const state = isRetrying ? "loading" : section.state

  return (
    <HomeTriageSection
      title={t("candidates.home_follow_up.title")}
      subtitle={t("candidates.home_follow_up.subtitle")}
      state={state}
      count={section.count}
      viewAllUrl={section.view_all_url}
      onRetry={handleRetry}
      isRetrying={isRetrying}
      testId="home-follow-up"
      // OKS-953 (PRD R7) — one of the FIVE candidate segments that collapse.
      collapseWhenEmpty
      emptyTestId="home-follow-up-empty"
      renderColdStart={() => (
        <EmptyState
          icon={Send}
          title={t("candidates.home_follow_up.cold_start.title")}
          titleAs="h2"
          description={t("candidates.home_follow_up.cold_start.description")}
          data-testid="home-follow-up-cold-start"
          actions={
            <Button asChild variant="outline">
              {/*
                Points at the `approved` tab — the bucket a brand with nobody
                contacted yet actually needs to work through.
              */}
              <Link href={candidatesPath({ status: "approved" })}>
                {t("candidates.home_follow_up.cold_start.cta")}
              </Link>
            </Button>
          }
        />
      )}
      renderEmpty={() => (
        <EmptyState
          icon={CheckCheck}
          title={t("candidates.home_follow_up.empty.title")}
          titleAs="h2"
          description={t("candidates.home_follow_up.empty.description")}
          data-testid="home-follow-up-empty"
        />
      )}
    >
      {section.items.map((item) => (
        <CandidateReviewCard key={item.id} item={item} />
      ))}
    </HomeTriageSection>
  )
}
